import { useState } from "react";
import { Alert, Badge, Button, Card, Field, Input, Select, Spinner } from "@empite/ui-components";
import type { AudienceTier, ProviderModel, ModelTestResult, Reason } from "../admin-client";
import { AUDIENCE_TIERS } from "../admin-client";
import {
  useProviderAccounts,
  useRoutingProfiles,
  useTestProviderModel,
} from "../admin-hooks";
import { ErrorAlert } from "./guards";
import { ReasonButton } from "./reason-prompt";

/**
 * "Does this model actually answer?", asked before a customer finds out.
 *
 * Two modes, because there are two different questions and an operator arrives
 * with one or the other:
 *
 *   Connectivity — does this credential reach a model that replies? Straight
 *   through the adapter, no routing, no price. This is the one to run after
 *   rotating a key or adding a catalogue row.
 *
 *   Routing — would a real request for this model succeed? Resolves the alias
 *   through a routing profile, so the policy chain, the token ceilings and the
 *   capability gates all apply, then prices what came back. This is the one to
 *   run before telling somebody the model is available.
 *
 * Neither reserves or settles, and the panel says so rather than leaving an
 * operator to assume the ledger was exercised. There is no operator wallet to
 * charge, and charging a customer's for a probe — or seeding a synthetic one
 * whose entries then live in the same append-only ledger every financial report
 * reads — would cost more than it proves.
 */
export function ModelTestPanel({
  row,
  onClose,
}: {
  row: ProviderModel;
  onClose: () => void;
}) {
  const test = useTestProviderModel();
  const accounts = useProviderAccounts();
  const profiles = useRoutingProfiles();

  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [maxTokens, setMaxTokens] = useState(String(DEFAULT_MAX_TOKENS));
  const [profileId, setProfileId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [audience, setAudience] = useState<AudienceTier>("internal");
  const [result, setResult] = useState<ModelTestResult | null>(null);

  const routing = profileId !== "";
  const name = row.display_name || row.model;

  // Only accounts that could answer: this model's provider, active, and
  // holding a live key. Offering one that cannot is offering a refusal an
  // operator would read as the model being broken.
  const usable = (accounts.data?.provider_accounts ?? []).filter(
    (account) =>
      account.provider === row.provider &&
      account.status === "active" &&
      account.active_credential_count > 0,
  );

  const tokens = Number(maxTokens.trim());
  const tokensError =
    !/^\d+$/.test(maxTokens.trim()) || tokens <= 0
      ? "Whole tokens, at least one."
      : tokens > MAX_TOKENS_CEILING
        ? `The ceiling is ${MAX_TOKENS_CEILING}. A test spends real upstream money, so the box is bounded rather than trusted.`
        : undefined;
  const promptError =
    prompt.trim() === ""
      ? "A test needs something to send."
      : [...prompt].length > MAX_PROMPT_CHARS
        ? `${[...prompt].length} characters, and the limit is ${MAX_PROMPT_CHARS}. This is a probe rather than a chat window.`
        : undefined;
  const ready = promptError === undefined && tokensError === undefined;

  async function run(reason: Reason) {
    const answer = await test.mutateAsync({
      modelID: row.id,
      run: {
        prompt: prompt.trim(),
        max_tokens: tokens,
        routing_profile_id: routing ? profileId : undefined,
        // Only in the mode that uses it. Sending an account id alongside a
        // profile would look like a choice the platform then ignored.
        provider_account_id: !routing && accountId !== "" ? accountId : undefined,
        audience: routing ? audience : undefined,
      },
      reason,
    });
    setResult(answer.result);
  }

  return (
    <div className="esm-subform">
      <h3>Test {name}</h3>
      <p className="esm-muted-text">
        Sends one prompt to <code>{row.model}</code> on <code>{row.provider}</code> and shows what
        comes back. This spends real upstream tokens. It does not reserve or settle against any
        wallet — there is no operator wallet to charge, so nothing here touches the ledger.
      </p>
      <ErrorAlert error={test.error} title="That test could not be run" />
      <div className="esm-form-grid">
        <Field
          label="Prompt"
          htmlFor="model-test-prompt"
          hint="Kept short on purpose. A long prompt spends upstream money to learn what a short one proves."
          error={promptError}
        >
          <textarea
            id="model-test-prompt"
            className="esm-input esm-textarea"
            rows={2}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
        </Field>
        <Field
          label="Reply limit"
          htmlFor="model-test-tokens"
          hint={`Tokens. At most ${MAX_TOKENS_CEILING}.`}
          error={tokensError}
        >
          <Input
            id="model-test-tokens"
            value={maxTokens}
            onChange={(event) => setMaxTokens(event.target.value)}
          />
        </Field>
        <Field
          label="Through"
          htmlFor="model-test-profile"
          hint={
            routing
              ? "Resolves the alias through this profile first, so the policy chain, the ceilings and the capability gates all apply — and prices what comes back. This is what a real request would do, minus the money."
              : "A plain connectivity check: straight to the provider on its own key, no routing and no price. Pick a routing profile to test what a real request would do."
          }
        >
          <Select
            id="model-test-profile"
            value={profileId}
            onChange={(event) => setProfileId(event.target.value)}
          >
            <option value="">The provider directly — connectivity only</option>
            {(profiles.data?.routing_profiles ?? []).map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.application_name} · {profile.name}
              </option>
            ))}
          </Select>
        </Field>
        {routing ? (
          <Field
            label="As"
            htmlFor="model-test-audience"
            hint="Whose eyes to resolve under. A model held back to the team is invisible to a live caller, and this is how to check that from the console rather than from a customer's report."
          >
            <Select
              id="model-test-audience"
              value={audience}
              onChange={(event) => setAudience(event.target.value as AudienceTier)}
            >
              {AUDIENCE_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {TIER_LABELS[tier]}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field
            label="Using the key from"
            htmlFor="model-test-account"
            hint="Which account's credential makes the call. Leave it to the platform unless you are checking one particular key."
          >
            <Select
              id="model-test-account"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">Any account with a live key</option>
              {usable.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.label} ({account.environment})
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={test.isPending}
          disabled={!ready}
          mutation={{
            title: `Test ${name}`,
            tone: "primary",
            confirmLabel: "Send the prompt",
            description: (
              <>
                <p>
                  One prompt to <code>{row.model}</code> on <code>{row.provider}</code>, capped at{" "}
                  {tokens} tokens. This spends real upstream tokens.
                </p>
                <p>
                  {routing
                    ? "It resolves through the chosen routing profile and prices the answer, but reserves and settles nothing."
                    : "It goes straight to the provider on its own credential. Nothing is routed and nothing is priced."}
                </p>
              </>
            ),
            run,
          }}
        >
          Run the test
        </ReasonButton>
        <Button variant="ghost" disabled={test.isPending} onClick={onClose}>
          Close
        </Button>
      </div>
      {test.isPending ? <Spinner label={`Asking ${row.provider}`} /> : null}
      {result ? <ModelTestAnswer result={result} /> : null}
    </div>
  );
}

/** What came back, and what it means. */
function ModelTestAnswer({ result }: { result: ModelTestResult }) {
  return (
    <Card
      title={result.failure === null ? "It answered" : "It refused"}
      actions={<Badge tone="neutral">{result.latency_ms} ms</Badge>}
    >
      {result.failure !== null ? (
        <Alert tone="danger" title={`The provider said ${result.failure.type}`}>
          {result.failure.message}
          {result.failure.retryable
            ? " The provider marked this retryable, so the same request may succeed shortly."
            : " The provider did not mark this retryable, so the same request will fail the same way."}
        </Alert>
      ) : (
        <>
          {/* The model's own words, preserved. A reply is the one thing an
              operator cannot check any other way — usage and latency say the
              call happened, and only the text says it worked. */}
          <pre className="esm-input esm-textarea">{result.text}</pre>
        </>
      )}
      {result.notice !== null ? (
        <Alert tone="warning" title="A caller would also be told this">
          {result.notice.message}
          {result.notice.sunset_at === null
            ? ""
            : ` It stops answering on ${new Date(result.notice.sunset_at).toLocaleDateString()}.`}
        </Alert>
      ) : null}
      <dl className="esm-form-grid">
        <div>
          <dt>Served by</dt>
          <dd>
            <code>{result.provider_model}</code> on <code>{result.provider}</code>
          </dd>
        </div>
        {result.alias === undefined ? null : (
          <div>
            <dt>Resolved from</dt>
            <dd>
              <code>{result.alias}</code>
              {result.chain && result.chain.length > 1 ? (
                <div className="esm-muted-text">
                  Chain: {result.chain.join(" → ")}. The first is what answered.
                </div>
              ) : null}
            </dd>
          </div>
        )}
        <div>
          <dt>Tokens</dt>
          <dd>
            {result.input_tokens} in / {result.output_tokens} out
          </dd>
        </div>
        <div>
          <dt>Would have cost</dt>
          <dd>
            {result.sell_price !== null ? (
              result.sell_price
            ) : result.mode === "routing" ? (
              // The finding this mode exists to surface: a model that answers
              // and cannot be charged for is one a customer will run for free
              // until somebody notices.
              <span className="esm-not-recorded">
                nothing — the price book has no live version for this pair
              </span>
            ) : (
              <span className="esm-not-recorded">
                not priced — pick a routing profile to price a test
              </span>
            )}
          </dd>
        </div>
      </dl>
      <p className="esm-muted-text">
        Nothing was reserved and nothing was settled: no wallet moved and no ledger entry was
        written. A live request for this model would additionally hold funds before the prompt left
        and settle against the provider's usage report afterwards.
      </p>
    </Card>
  );
}

const DEFAULT_PROMPT = "Say hello to the world.";
const DEFAULT_MAX_TOKENS = 64;

/** Mirrors the server's own bounds, so an operator hears the rule while typing
 *  rather than after spending. The server applies the same two. */
const MAX_PROMPT_CHARS = 2000;
const MAX_TOKENS_CEILING = 512;

const TIER_LABELS: Record<AudienceTier, string> = {
  internal: "Empite only",
  tester: "An external tester",
  live: "An ordinary customer",
};
